import { Box, Heading, Text, Transition } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import React, { FC, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { clientSdk } from '../../../common/ClientSDK';
import { Page, useCurrentPage } from '../../../common/hooks/useCurrentPage';
import {
  selectHomepageHeader,
  selectScrolledToTop,
} from '../../../common/selectors/homepage';
import { selectUserName } from '../../../common/selectors/user';
import { TLocalesKeys } from '../../../types';
import { renderModel } from '../../../util/renderModel';
import { BackButton } from '../BackButton';
import { HeaderActions } from '../HeaderActions';
import { DragIcon } from './DragIcon';
import { headerDataHooks } from './Header.dataHooks';
import s from './styles.scss';
import { useHeaderTitle } from './useHeaderTitle';

const EXIT_USERNAME_ANIMATION = {
  moveOut: {
    direction: 'bottomToTop',
    distance: '15px',
    duration: 'fast02',
    easing: 'exitEasing',
    delay: 'short',
  },
} as const;

const ENTER_USERNAME_ANIMATION = {
  moveIn: {
    direction: 'topToBottom',
    distance: '15px',
    duration: 'fast02',
    easing: 'enterEasing',
  },
} as const;

export const HomepageHeader: FC = () => {
  const { t } = useTranslation();
  const userName = useSelector(selectUserName);
  const homepageScrolledToTop = useSelector(selectScrolledToTop);
  const homepageHeader = useSelector(selectHomepageHeader);
  const currentPage = useCurrentPage();
  const headerTitle = useHeaderTitle();

  const withUsername = useMemo(
    () => currentPage === Page.Home && homepageHeader?.showUsername,
    [homepageHeader, currentPage],
  );
  const headerUsername =
    (userName && `${userName.first}`.trim()) ||
    t<TLocalesKeys>('header_user_partner');

  const startDrag = useCallback((event) => {
    // Prevent changing focus
    event.preventDefault();

    const { clientX, clientY } = event;
    clientSdk.startDrag({ clientX, clientY });
  }, []);

  return (
    <Box direction="vertical" className={s.header}>
      {renderModel.isInvokedFromHeader() ? (
        <Text
          dataHook={headerDataHooks.dragByIconArea()}
          light
          secondary
          onMouseDown={startDrag}
          className={s.draggableDotsContainer}
        >
          <DragIcon />
        </Text>
      ) : null}
      <Box gap={1} className={s.headerContainer}>
        <BackButton />
        <div
          onMouseDown={startDrag}
          className={s.draggableArea}
          data-hook={headerDataHooks.dragByHeaderArea()}
        >
          <Box direction="vertical">
            {withUsername && (
              <Transition
                show={homepageScrolledToTop}
                exitAnimation={EXIT_USERNAME_ANIMATION}
                enterAnimation={ENTER_USERNAME_ANIMATION}
              >
                <Heading
                  size="medium"
                  light
                  dataHook={headerDataHooks.greetingText()}
                >
                  {t<TLocalesKeys>('header_user_greeting', {
                    username: headerUsername,
                  })}
                </Heading>
              </Transition>
            )}
            <Heading size="medium" light dataHook={headerDataHooks.titleText()}>
              {headerTitle}
            </Heading>
          </Box>
        </div>
        <HeaderActions />
      </Box>
    </Box>
  );
};
